<template>
  <template v-if="model.state.section.enabled && allowRenderParticles">
    <teleport :to="model.state.section.selectorClass">
      <ParticleCanvas :config="particlesConfig" />
    </teleport>
  </template>
  <template v-else-if="model.state.flowpage.enabled && allowRenderParticles">
    <teleport to=".flow">
      <ParticleCanvas :config="particlesConfig" />
    </teleport>
  </template>
  <template v-else-if="model.state.body.enabled && allowRenderParticles">
    <teleport to="#app">
      <ParticleCanvas :config="particlesConfig" />
    </teleport>
  </template>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent, onMounted, ref } from 'vue';
import type { ISourceOptions } from '@tsparticles/engine';
import type { IntegrationParticleGeneratorModel } from '@/src/components/integrations/particle-generator/Model';
import {
  ParticleGeneratorLocation,
  ParticleGeneratorVariant
} from '@/src/components/integrations/particle-generator/Model';
import {
  getConfettiConfig,
  getCustomConfig,
  getFlagsConfig,
  getSnowConfig
} from '@/src/components/integrations/particle-generator/particle-configs';
import { useCampaignStore } from '@/src/store/campaign';
import { preloadImagePromise } from '@/src/utilities/Utilities';
import ParticleCanvas from '@/src/components/integrations/particle-generator/ParticleCanvas.vue';

export default defineComponent({
  name: 'integration-particle-generator',
  components: { ParticleCanvas },
  props: {
    model: {
      type: Object as PropType<IntegrationParticleGeneratorModel>,
      required: true
    }
  },
  setup(props) {
    const campaignStore = useCampaignStore();
    const isReady = ref(false);

    const baseConfig: ISourceOptions = {
      autoPlay: true,
      background: {
        color: {
          value: 'none'
        },
        opacity: 1
      },
      fullScreen: {
        enable: true,
        zIndex: -1
      }
    };

    if (props.model.state.section.enabled && baseConfig.fullScreen) {
      baseConfig.fullScreen = {
        enable: false,
        zIndex: -1
      };
    }

    const particlesConfig = computed<ISourceOptions>(() => {
      if (props.model.state.variant === ParticleGeneratorVariant.CUSTOM) {
        return getCustomConfig(props.model.state);
      } else if (props.model.state.variant === ParticleGeneratorVariant.CONFETTI) {
        return getConfettiConfig(props.model.state);
      } else if (props.model.state.variant === ParticleGeneratorVariant.FLAGS) {
        return getFlagsConfig(props.model.state);
      }

      return getSnowConfig(props.model.state);
    });

    const allowRenderParticles = computed(() => {
      if (props.model.state.body?.enabled) {
        if (props.model.state.body.onSpecificFlowPages && props.model.state.body.flowPages) {
          return props.model.state.body.flowPages?.some((flowPageId) =>
            campaignStore.mountedFlowpages.includes(flowPageId)
          );
        }

        return true;
      }

      if (props.model.state.flowpage.enabled) {
        return props.model.state.flowpage.ids?.some((flowPageId) =>
          campaignStore.mountedFlowpages.includes(flowPageId)
        );
      }

      return !!(
        props.model.state.section.enabled &&
        props.model.state.section.id &&
        campaignStore.mountedSections.includes(props.model.state.section.id)
      );
    });

    onMounted(async () => {
      let hasError = false;
      if (props.model.state.variant === ParticleGeneratorVariant.CUSTOM) {
        if (props.model.state.particle.images?.length) {
          for (const image of props.model.state.particle.images) {
            await preloadImagePromise(image, 'anonymous').catch(() => {
              hasError = true;
            });
          }
        } else {
          hasError = true;
        }
      }

      if (!hasError) {
        isReady.value = true;
      }
    });

    return {
      isReady,
      particlesConfig,
      allowRenderParticles,
      ParticleGeneratorLocation
    };
  }
});
</script>

<style lang="scss">
.tsparticles {
  opacity: 0;
  animation-name: fadeIn;
  animation-duration: 1s;
  animation-fill-mode: forwards;
  animation-timing-function: ease;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  pointer-events: none;

  canvas {
    width: 100% !important;
    height: 100% !important;
  }

  .site--iframe.site--popover-active.site--mobile & {
    display: none;
  }

  &__in-section,
  &__in-flowpage {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 20;
    pointer-events: none;
    overflow: hidden;
  }
}
</style>
